import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Calendar as MultiDatePicker } from "react-multi-date-picker";
import Swal from "sweetalert2";
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from "react-redux";
import { updateLeave, updateLeaveRange, cancelAndBookLeaveRange, deleteLeave, getAllLeave, bookedAppointmentCheck, getLeaveGroups, deleteLeaveGroup } from "../../redux/doctorSlice";
import { FEATURE_KEYS, LEAVE_FUTURE_LIMIT_OPTIONS, getFeatureValue } from "../../redux/featureSlice";
import { addMinutes } from "date-fns";
import CommonLoader from "../../components/CommonLoader"; // Adjust path as needed
import emptykey from "../../assets/img/colorkit (3).svg"; // Replace with actual path to the image
import { Calendar, Edit, Trash2, Sun, CloudSun, Cloud, ArrowLeft, ArrowRight, X, AlertCircle } from 'lucide-react';
import MasterLabel from '../common/MasterLabel';
import WhoLabel from '../common/WhoLabel';
import { readLeaveRefusal, leaveBookingsQuestion, leaveRefusalSentence } from './leaveRefusal';
import { formatClock, formatTime } from '../../utils/clock';

// The visits a leave may not silently sit on top of — the server's
// LEAVE_BLOCKING_STATUSES (P18). A visit waiting on the patient's answer to a
// reschedule still holds its slot, so the pre-check lists it too; otherwise the
// screen says "no appointments" and the save comes back 409.
const LEAVE_BLOCKING_STATUSES = "Booked,Rescheduling";

// The clock window each leave period covers, as the leave endpoints store it.
const PERIOD_TIMES = {
    forenoon: { startTime: "00:00", endTime: "11:59" },
    afternoon: { startTime: "12:00", endTime: "23:59" },
    fullDay: { startTime: "00:00", endTime: "23:59" },
};

// Format an appointment's start time in the viewer's current timezone.
// Prefers `localTime` (ISO-8601 with offset) so the UTC instant converts correctly
// to France / India / any browser TZ. Falls back to the stored `timeSlot` string.
// Shown in the one clock format, "2:30 PM" (owner, 15 Oct 2026).
const formatAppointmentTime = (appointment) => {
    if (!appointment) return '';
    const source = appointment.localTime || appointment.scheduledDate;
    if (source) {
        const shown = formatTime(source);
        if (shown) return shown;
    }
    return formatClock(appointment.timeSlot);
};

const Leavedatescal = () => {
    const { t, i18n } = useTranslation(["leavedatescal"]);
    const navigate = useNavigate();
    const { selectedDoctor, leaves, leaveLoading, loading, bookedAppointments, bookedAppointmentsLoading, leaveGroups } = useSelector((state) => state.doctor);
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDates, setSelectedDates] = useState([]);
    const [selectedTimes, setSelectedTimes] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [editingDate, setEditingDate] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState("");
    const [showAppointments, setShowAppointments] = useState(false);
    const [leaveMode, setLeaveMode] = useState("single"); // "single" or "range"
    const [toDate, setToDate] = useState(null);
    const [rangeDayPeriods, setRangeDayPeriods] = useState({}); // { "2026-04-03": "fullDay", "2026-04-04": "forenoon", ... }
    const [rangeDayAppointments, setRangeDayAppointments] = useState({}); // { "2026-04-03": [...appointments], ... }
    const [rangeDayLoading, setRangeDayLoading] = useState({}); // { "2026-04-03": true, ... }
    const [editingGroupId, setEditingGroupId] = useState(null); // group being edited
    const [mergeConfirmed, setMergeConfirmed] = useState(false); // user accepted merging existing leaves into new range
    // Public-holiday marking. One flag + one name applies to the whole leave —
    // for a date range, the single name covers every day in the range.
    const [isPublicHoliday, setIsPublicHoliday] = useState(false);
    const [holidayName, setHolidayName] = useState("");
    const doctorId = selectedDoctor?.doctorId;

    const getFlattenedLeaves = () => {
        if (!leaves?.months) return [];
        return Object.values(leaves.months).flat();
    };

    const leaveDates = getFlattenedLeaves();

    // Calculate start year from doctor's creation date
    const startYear = useMemo(() => {
        if (selectedDoctor?.createdAt) {
            const createdDate = new Date(selectedDoctor.createdAt);
            return createdDate.getFullYear();
        }
        return new Date().getFullYear(); // fallback to current year
    }, [selectedDoctor?.createdAt]);

    // How far ahead a doctor may apply for leave — admin-configurable via
    // Settings → Doctor → "Leave Apply Future Upto" (feature value
    // '3'|'6'|'9'|'12'|'none'). Defaults to 12 months when unset.
    const leaveFutureLimitValue = useSelector((state) =>
        getFeatureValue(state, FEATURE_KEYS.doctorLeaveFutureLimit)
    );

    // Max selectable leave date, derived from the configured limit.
    // 'No restriction' is capped at a practical 5 years so the calendar and
    // year navigation stay sane.
    const maxLeaveDate = useMemo(() => {
        const option = LEAVE_FUTURE_LIMIT_OPTIONS.find((o) => o.value === leaveFutureLimitValue);
        const months = option ? (option.months ?? 60) : 12;
        const d = new Date();
        d.setMonth(d.getMonth() + months);
        return d;
    }, [leaveFutureLimitValue]);

    // End year should cover the configured range (e.g., if today is Oct 2026 and
    // the limit is 6 months, max is Apr 2027)
    const endYear = maxLeaveDate.getFullYear();

    // Custom locale configuration using translations from JSON
    // According to React Multi Date Picker 4.5.2 docs, weekDays should be properly structured
    const customLocale = useMemo(() => {
        const weekdays = t('leavedatescal.weekdays', { returnObjects: true });
        const months = t('leavedatescal.months', { returnObjects: true });
        
        const defaultMonths = [
            ["January", "Jan"],
            ["February", "Feb"],
            ["March", "Mar"],
            ["April", "Apr"],
            ["May", "May"],
            ["June", "Jun"],
            ["July", "Jul"],
            ["August", "Aug"],
            ["September", "Sep"],
            ["October", "Oct"],
            ["November", "Nov"],
            ["December", "Dec"]
        ];
        
        // Use weekdays from translation in Sunday-first order (library native order)
        // The library handles Monday-first display via weekStartDayIndex={1}
        const defaultWeekdaysSundayFirst = [
            ["Sunday", "Sun"],
            ["Monday", "Mon"],
            ["Tuesday", "Tue"],
            ["Wednesday", "Wed"],
            ["Thursday", "Thu"],
            ["Friday", "Fri"],
            ["Saturday", "Sat"]
        ];
        let weekDaysArray = Array.isArray(weekdays) && weekdays.length === 7 ? [...weekdays] : [...defaultWeekdaysSundayFirst];

        // Ensure the array is in Sunday-first order (library's native order)
        const sundayIndex = weekDaysArray.findIndex(day => {
            const fullName = day?.[0]?.toLowerCase();
            const abbr = day?.[1]?.toLowerCase();
            return fullName === 'sunday' || fullName === 'dimanche' ||
                   abbr === 'sun' || abbr === 'dim';
        });

        if (sundayIndex > 0) {
            weekDaysArray = [
                ...weekDaysArray.slice(sundayIndex),
                ...weekDaysArray.slice(0, sundayIndex)
            ];
        } else if (sundayIndex === -1) {
            weekDaysArray = [...defaultWeekdaysSundayFirst];
        }
        
        const monthsArray = Array.isArray(months) && months.length === 12 ? months : defaultMonths;
        
        return {
            name: i18n.language === 'fr' ? 'gregorian_fr_custom' : 'gregorian_en_custom',
            months: monthsArray,
            weekDays: weekDaysArray,
            digits: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
            meridiems: [['AM', 'am'], ['PM', 'pm']]
        };
    }, [i18n.language, t]);

    // Extract weekDays for direct prop usage (recommended by docs)
    const weekDaysArray = useMemo(() => {
        return customLocale.weekDays || [
            ["Sunday", "Sun"],
            ["Monday", "Mon"],
            ["Tuesday", "Tue"],
            ["Wednesday", "Wed"],
            ["Thursday", "Thu"],
            ["Friday", "Fri"],
            ["Saturday", "Sat"]
        ];
    }, [customLocale.weekDays]);

    // Fetch appointments only when period is changed (not on initial date selection)
    const fetchAppointments = (formattedDate, period) => {
        if (!doctorId) return;

        // Get time range based on period
        let startTime, endTime;
        switch (period) {
            case "forenoon":
                startTime = "00:00";
                endTime = "11:59";
                break;
            case "afternoon":
                startTime = "12:00";
                endTime = "23:59";
                break;
            case "fullDay":
                startTime = "00:00";
                endTime = "23:59";
                break;
            default:
                startTime = "00:00";
                endTime = "23:59";
        }

        // Create ISO date strings in local timezone
        const startDate = new Date(`${formattedDate}T${startTime}:00`).toISOString();
        const endDate = new Date(`${formattedDate}T${endTime}:59`).toISOString();

        // Fetch appointments using the new bookedAppointmentCheck function
        const queryParams = {
            doctorId,
            startDate,
            endDate,
            appointmentStatus: LEAVE_BLOCKING_STATUSES,
            page: 1,
            limit: 100
        };

        dispatch(bookedAppointmentCheck(queryParams)).then((result) => {
            if (result.payload && result.payload.appointments && result.payload.appointments.length > 0) {
                setShowAppointments(true);
            } else {
                setShowAppointments(false);
            }
        }).catch(() => {
            setShowAppointments(false);
        });
    };

    const currentYearDisplayed = parseInt(leaves?.year || startYear);

    // Restrict navigation to doctor's creation year through current year
    const canGoPrev = currentYearDisplayed > startYear;
    const canGoNext = currentYearDisplayed < endYear;

    // Fetch leave groups whenever the displayed year changes
    useEffect(() => {
        if (doctorId && currentYearDisplayed) {
            dispatch(getLeaveGroups({ doctorId, year: currentYearDisplayed }));
        }
    }, [doctorId, currentYearDisplayed, dispatch]);

    const handlePrevYear = () => {
        if (canGoPrev) {
            const prevYear = currentYearDisplayed - 1;
            dispatch(getAllLeave({ doctorId, year: prevYear }));
        }
    };

    const handleNextYear = () => {
        if (canGoNext) {
            const nextYear = currentYearDisplayed + 1;
            dispatch(getAllLeave({ doctorId, year: nextYear }));
        }
    };

    const handleDateChange = (value) => {
        setShowAppointments(false);
        setSelectedPeriod("");
        if (!value) {
            setSelectedDates([]);
            setSelectedTimes({});
            setSelectedPeriod("");
            return;
        }
        const newDate = new Date(value);
        // Use local date methods to avoid timezone issues
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, '0');
        const day = String(newDate.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        if (
            leaveDates.some(
                (item) => item.date === formattedDate && !editingDate
            )
        ) {
            toast.warn(t("leavedatescal.validation_existing_date"));
            setSelectedDates([]);
            setSelectedTimes({});
            setSelectedPeriod("");
            return;
        }
        setSelectedDates([newDate]);
        setSelectedTimes((prevTimes) => ({
            ...prevTimes,
            [formattedDate]: {
                startTime: editingDate ? prevTimes[formattedDate]?.startTime : "00:00",
                endTime: editingDate ? prevTimes[formattedDate]?.endTime : "23:59",
            },
        }));
        setSelectedPeriod(editingDate ? selectedPeriod : "");
        setHasUnsavedChanges(true);
    };

    const handlePeriodChange = (period, formattedDate) => {
        setSelectedPeriod(period);
        let times;
        switch (period) {
            case "forenoon":
                times = { startTime: "00:00", endTime: "11:59" };
                break;
            case "afternoon":
                times = { startTime: "12:00", endTime: "23:59" };
                break;
            case "fullDay":
                times = { startTime: "00:00", endTime: "23:59" };
                break;
            default:
                times = { startTime: "", endTime: "" };
        }
        setSelectedTimes((prevTimes) => ({
            ...prevTimes,
            [formattedDate]: times,
        }));
        setHasUnsavedChanges(true);

        // Fetch appointments when period is selected
        fetchAppointments(formattedDate, period);
    };

    const validateTimes = () => {
        // Use local date methods to avoid timezone issues
        const date = selectedDates[0];
        if (!date) return { isValid: false, message: t("leavedatescal.validation_missing_date") };
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        const times = selectedTimes[formattedDate];
        if (selectedPeriod !== "fullDay" && (!times || !times.startTime || !times.endTime)) {
            return { isValid: false, message: t("leavedatescal.validation_missing_times") };
        }
        if (selectedPeriod !== "fullDay") {
            const [startHour, startMinute] = times.startTime.split(":").map(Number);
            const [endHour, endMinute] = times.endTime.split(":").map(Number);
            const startTotalMinutes = startHour * 60 + startMinute;
            const endTotalMinutes = endHour * 60 + endMinute;
            if (startTotalMinutes >= endTotalMinutes) {
                return { isValid: false, message: t("leavedatescal.validation_end_before_start") };
            }
        }
        return { isValid: true };
    };

    const handleAddEditClick = () => {
        setIsModalOpen(true);
        setEditingDate(null);
        setSelectedDates([]);
        setSelectedTimes({});
        setSelectedPeriod("");
        setHasUnsavedChanges(false);
        setLeaveMode("single");
        setToDate(null);
        setShowAppointments(false);
        resetHolidayState();
    };

    const handleEditSpecific = (item) => {
        const date = new Date(item.date);
        // Use local date methods to avoid timezone issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        setSelectedDates([date]);
        const startTime = item.startTime;
        const endTime = item.endTime;
        let period = "fullDay";
        if (startTime === "00:00" && (endTime === "11:59" || endTime === "12:00")) {
            period = "forenoon";
        } else if (startTime === "12:00" && endTime === "23:59") {
            period = "afternoon";
        }
        setSelectedTimes({
            [formattedDate]: {
                startTime,
                endTime,
            },
        });
        setSelectedPeriod(period);
        setEditingDate(formattedDate);
        setIsModalOpen(true);
        setHasUnsavedChanges(false);
        setShowAppointments(false);

        // Pre-fill public-holiday flag/name from the saved leave.
        setIsPublicHoliday(!!item.isPublicHoliday);
        setHolidayName(item.holidayName || "");

        // Fetch appointments for the selected period when editing
        fetchAppointments(formattedDate, period);
    };

    // Edit a bulk leave group — open modal in range mode with pre-filled data
    const handleEditGroup = (group) => {
        const fromDate = new Date(group.fromDate + "T00:00:00");
        const toDateVal = new Date(group.toDate + "T00:00:00");

        // Pre-fill range day periods from group data
        const periods = {};
        group.days.forEach(day => {
            periods[day.date] = day.period;
        });
        // Whole-range public holiday: read the shared flag/name off any flagged day.
        const holidayDay = group.days.find(day => day.isPublicHoliday);

        setLeaveMode("range");
        setSelectedDates([fromDate]);
        setToDate(toDateVal);
        setRangeDayPeriods(periods);
        setRangeDayAppointments({});
        setRangeDayLoading({});
        setEditingGroupId(group.groupId);
        setMergeConfirmed(false);
        setIsModalOpen(true);
        setHasUnsavedChanges(false);

        setIsPublicHoliday(!!holidayDay);
        setHolidayName(holidayDay ? (holidayDay.holidayName || "") : "");
    };

    const handleDelete = async (item) => {
        const leaveId = item.leaveId || null;

        if (!leaveId) {
            toast.warn(t("leavedatescal.delete_no_leaveid"));
            return;
        }

        const result = await Swal.fire({
            title: t("leavedatescal.delete_confirm_title"),
            text: t("leavedatescal.delete_confirm_text"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("leavedatescal.delete_confirm_yes"),
            cancelButtonText: t("leavedatescal.delete_confirm_no"),
            confirmButtonColor: "#4ABDBD",
            cancelButtonColor: "#d33",
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteLeave({ leaveId, doctorId })).unwrap();
                dispatch(getAllLeave({ doctorId, year: leaves.year }));
                dispatch(getLeaveGroups({ doctorId, year: leaves.year }));
                toast.success(t("leavedatescal.delete_success_text"));
            } catch (error) {
                toast.warn(error?.response?.data?.message?.en || error?.message?.en || (typeof error?.message === 'string' ? error.message : null) || t("leavedatescal.delete_error_text"));
            }
        }
    };

    // ---- Saving a leave: the one question, and the refusals as sentences (P18) ----

    const leaveYear = () => leaves?.year || currentYearDisplayed;

    const refreshLeaveLists = () => {
        dispatch(getAllLeave({ doctorId, year: leaveYear() }));
        dispatch(getLeaveGroups({ doctorId, year: leaveYear() }));
    };

    // The server found live visits inside the leave. There are exactly two
    // answers — cancel them and notify each patient, or keep the leave off.
    // (The old third one, "save with active appointments", saved the leave and
    // told nobody; the server no longer honours it and this screen no longer
    // offers it.) Resolves true for "cancel and notify".
    const askCancelAndNotify = async (refusal) => {
        const { title, text } = leaveBookingsQuestion(refusal, t, i18n.language);
        const answer = await Swal.fire({
            title,
            text,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("leavedatescal.cancel_and_notify_button", "Cancel and notify"),
            cancelButtonText: t("leavedatescal.keep_leave_off_button", "Keep the leave off"),
            confirmButtonColor: "#d33",
            cancelButtonColor: "#9ca3af",
        });
        return !!answer?.isConfirmed;
    };

    const leaveSavedToast = (result, fallbackText) => {
        const cancelled = Number(result?.totalCancelled) || 0;
        toast.success(cancelled > 0
            ? t("leavedatescal.leave_saved_visits_cancelled", {
                n: cancelled,
                defaultValue: "Leave saved. {{n}} booked visit(s) cancelled and the patients notified.",
            })
            : fallbackText);
    };

    // One single-day leave write. A bookings conflict is asked about once;
    // "cancel and notify" resends the same leave with cancelAndNotify. Every
    // other refusal is shown as its sentence and the form stays open.
    const saveSingleLeave = async (entry, successText) => {
        try {
            const result = await dispatch(updateLeave({ doctorId, leaveData: entry })).unwrap();
            refreshLeaveLists();
            handleCancel();
            leaveSavedToast(result, successText);
        } catch (rejected) {
            const refusal = readLeaveRefusal(rejected);
            if (refusal.kind === "hasBookings" && !entry.cancelAndNotify) {
                if (await askCancelAndNotify(refusal)) {
                    await saveSingleLeave({ ...entry, cancelAndNotify: true }, successText);
                }
                return;
            }
            toast.warn(leaveRefusalSentence(refusal, t, i18n.language, t("leavedatescal.save_error_text")));
        }
    };

    // The single-day leave the form describes, ready to send.
    const buildSingleEntry = () => {
        const selectedDate = selectedDates[0];
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const formattedDateKey = `${year}-${month}-${day}`;
        const times = PERIOD_TIMES[selectedPeriod]
            || selectedTimes[formattedDateKey]
            || PERIOD_TIMES.fullDay;

        const entry = {
            date: addMinutes(selectedDate, 330).toISOString(),
            startTime: times.startTime,
            endTime: times.endTime,
            timeZone: selectedDoctor?.timeZone,
            isPublicHoliday: !!isPublicHoliday,
            holidayName: isPublicHoliday ? holidayName.trim() : "",
        };
        if (editingDate) {
            entry.leaveId = leaveDates.find((item) => item.date === editingDate)?.leaveId;
        }
        return entry;
    };

    const handleSave = async () => {
        const validation = validateTimes();
        if (!validation.isValid) {
            toast.warn(validation.message);
            return;
        }

        // Public holiday must be named when the doctor flags it.
        if (holidayNameMissing()) return;

        const newEntry = buildSingleEntry();

        const result = await Swal.fire({
            title: t("leavedatescal.save_confirm_title"),
            text: t("leavedatescal.save_confirm_text"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("leavedatescal.save_confirm_yes"),
            cancelButtonText: t("leavedatescal.save_confirm_no"),
            confirmButtonColor: "#4ABDBD",
            cancelButtonColor: "#d33",
        });

        if (result.isConfirmed) {
            await saveSingleLeave(newEntry, t("leavedatescal.save_success_text"));
        }
    };

    // Get all dates between from and to
    // Get the leaveIds belonging to the group currently being edited
    const editingGroupLeaveIds = useMemo(() => {
        if (!editingGroupId || !leaveGroups) return new Set();
        const group = leaveGroups.find(g => g.groupId === editingGroupId);
        return new Set(group?.leaveIds || []);
    }, [editingGroupId, leaveGroups]);

    // Determine the period of an existing single leave from its start/end times
    const periodFromTimes = (startTime, endTime) => {
        if (startTime === "00:00" && (endTime === "11:59" || endTime === "12:00")) return "forenoon";
        if (startTime === "12:00" && endTime === "23:59") return "afternoon";
        return "fullDay";
    };

    // Detect existing individual leaves and bulk groups overlapping the selected range.
    // Excludes the leaves/group currently being edited so editing behaves the same as before.
    const overlapInfo = useMemo(() => {
        if (leaveMode !== "range" || !selectedDates[0] || !toDate) {
            return { individualLeaves: [], groups: [] };
        }
        const fmt = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };
        const fromStr = fmt(selectedDates[0]);
        const toStr = fmt(toDate);

        const groupedLeaveIds = new Set();
        (leaveGroups || []).forEach(g => g.leaveIds.forEach(id => groupedLeaveIds.add(id)));

        const individualLeaves = (leaveDates || []).filter(item => {
            if (groupedLeaveIds.has(item.leaveId)) return false;
            if (editingGroupLeaveIds.has(item.leaveId)) return false;
            return item.date >= fromStr && item.date <= toStr;
        });

        const groups = (leaveGroups || []).filter(g => {
            if (g.groupId === editingGroupId) return false;
            // Range intersection: not (groupEnd < newStart || groupStart > newEnd)
            return !(g.toDate < fromStr || g.fromDate > toStr);
        });

        return { individualLeaves, groups };
    }, [leaveMode, selectedDates, toDate, leaveDates, leaveGroups, editingGroupId, editingGroupLeaveIds]);

    const hasOverlap = overlapInfo.individualLeaves.length > 0 || overlapInfo.groups.length > 0;

    // Map of date -> period for the existing leaves that get merged into the new range.
    // Drives both pre-fill and lock UI in the timeline.
    const lockedRangeDates = useMemo(() => {
        if (!mergeConfirmed) return {};
        const map = {};
        overlapInfo.individualLeaves.forEach(item => {
            map[item.date] = periodFromTimes(item.startTime, item.endTime);
        });
        overlapInfo.groups.forEach(g => {
            g.days.forEach(d => { map[d.date] = d.period; });
        });
        return map;
    }, [mergeConfirmed, overlapInfo]);

    // Reset merge confirmation when leave mode changes (single ↔ range).
    // Date-bound changes are handled directly in the calendar's onChange so that
    // programmatic bound extension during merge doesn't unset the confirmation.
    useEffect(() => {
        setMergeConfirmed(false);
    }, [leaveMode]);

    const handleMergeConfirm = () => {
        // Extend visible range bounds to cover any group that hangs outside the picked range.
        // Iterate to a fixed point so cascading partial overlaps are absorbed in one click.
        if (!selectedDates[0] || !toDate) {
            setMergeConfirmed(true);
            return;
        }
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        let from = selectedDates[0];
        let to = toDate;
        let changed = true;
        while (changed) {
            changed = false;
            const fromStr = fmt(from);
            const toStr = fmt(to);
            (leaveGroups || []).forEach(g => {
                if (g.groupId === editingGroupId) return;
                if (g.toDate < fromStr || g.fromDate > toStr) return;
                const gFrom = new Date(g.fromDate + "T00:00:00");
                const gTo = new Date(g.toDate + "T00:00:00");
                if (gFrom < from) { from = gFrom; changed = true; }
                if (gTo > to) { to = gTo; changed = true; }
            });
        }

        // Compute final overlap against the (possibly extended) bounds so we pre-fill all merged dates
        const fromStr = fmt(from);
        const toStr = fmt(to);
        const groupedLeaveIds = new Set();
        (leaveGroups || []).forEach(g => g.leaveIds.forEach(id => groupedLeaveIds.add(id)));
        const finalIndividualLeaves = (leaveDates || []).filter(item => {
            if (groupedLeaveIds.has(item.leaveId)) return false;
            if (editingGroupLeaveIds.has(item.leaveId)) return false;
            return item.date >= fromStr && item.date <= toStr;
        });
        const finalGroups = (leaveGroups || []).filter(g => {
            if (g.groupId === editingGroupId) return false;
            return !(g.toDate < fromStr || g.fromDate > toStr);
        });

        // Pre-fill periods once at merge time. After this, the user can freely edit them.
        const prefill = {};
        finalIndividualLeaves.forEach(item => {
            prefill[item.date] = periodFromTimes(item.startTime, item.endTime);
        });
        finalGroups.forEach(g => {
            g.days.forEach(d => { prefill[d.date] = d.period; });
        });

        if (from !== selectedDates[0]) setSelectedDates([from]);
        if (to !== toDate) setToDate(to);
        setRangeDayPeriods(prev => ({ ...prev, ...prefill }));
        setMergeConfirmed(true);
        setHasUnsavedChanges(true);
    };

    const handleUndoMerge = () => {
        // Remove the locked dates' pre-filled periods so the timeline reverts to "before merge" state
        setRangeDayPeriods(prev => {
            const updated = { ...prev };
            Object.keys(lockedRangeDates).forEach(date => {
                delete updated[date];
            });
            return updated;
        });
        setRangeDayAppointments(prev => {
            const updated = { ...prev };
            Object.keys(lockedRangeDates).forEach(date => {
                delete updated[date];
            });
            return updated;
        });
        setMergeConfirmed(false);
    };

    const getRangeDates = () => {
        if (!selectedDates[0] || !toDate) return [];
        const dates = [];
        const current = new Date(selectedDates[0]);
        const end = new Date(toDate);
        while (current <= end) {
            const y = current.getFullYear();
            const m = String(current.getMonth() + 1).padStart(2, '0');
            const d = String(current.getDate()).padStart(2, '0');
            const dateStr = `${y}-${m}-${d}`;
            const bookedItem = leaveDates.find((item) => item.date === dateStr);
            const isInEditingGroup = bookedItem && editingGroupLeaveIds.has(bookedItem.leaveId);
            const isMergedDate = mergeConfirmed && lockedRangeDates[dateStr] !== undefined;
            if (!bookedItem || isInEditingGroup || isMergedDate) {
                dates.push(dateStr);
            }
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    // Handle period change for a specific day in range mode (toggle on re-click)
    const handleRangeDayPeriodChange = (dateStr, period) => {
        // If same period is clicked again, unselect it
        if (rangeDayPeriods[dateStr] === period) {
            setRangeDayPeriods(prev => {
                const updated = { ...prev };
                delete updated[dateStr];
                return updated;
            });
            setRangeDayAppointments(prev => {
                const updated = { ...prev };
                delete updated[dateStr];
                return updated;
            });
            return;
        }
        setRangeDayPeriods(prev => ({ ...prev, [dateStr]: period }));

        // Fetch appointments for this day
        let startTime, endTime;
        switch (period) {
            case "forenoon": startTime = "00:00"; endTime = "11:59"; break;
            case "afternoon": startTime = "12:00"; endTime = "23:59"; break;
            case "fullDay": default: startTime = "00:00"; endTime = "23:59"; break;
        }

        const startDate = new Date(`${dateStr}T${startTime}:00`).toISOString();
        const endDate = new Date(`${dateStr}T${endTime}:59`).toISOString();

        setRangeDayLoading(prev => ({ ...prev, [dateStr]: true }));

        dispatch(bookedAppointmentCheck({
            doctorId,
            startDate,
            endDate,
            appointmentStatus: LEAVE_BLOCKING_STATUSES,
            page: 1,
            limit: 100
        })).then((result) => {
            const appts = result.payload?.appointments || [];
            setRangeDayAppointments(prev => ({ ...prev, [dateStr]: appts }));
            setRangeDayLoading(prev => ({ ...prev, [dateStr]: false }));
        }).catch(() => {
            setRangeDayAppointments(prev => ({ ...prev, [dateStr]: [] }));
            setRangeDayLoading(prev => ({ ...prev, [dateStr]: false }));
        });
    };

    // ---- Public-holiday helpers ----

    // Reset public-holiday state. Called on open/close/mode-switch.
    const resetHolidayState = () => {
        setIsPublicHoliday(false);
        setHolidayName("");
    };

    // Toggle the "public holiday" checkbox; clear the name when unchecked.
    const handleHolidayToggle = (checked) => {
        setIsPublicHoliday(checked);
        if (!checked) setHolidayName("");
        setHasUnsavedChanges(true);
    };

    // Warns + blocks (returns true) when the public holiday is flagged but unnamed.
    // Shared by the single-day and range save paths.
    const holidayNameMissing = () => {
        if (isPublicHoliday && !holidayName.trim()) {
            toast.warn(t("leavedatescal.validation_holiday_name_required", "Please enter the name of this public holiday."));
            return true;
        }
        return false;
    };

    // Set all range days to a period at once (toggle: if all already have this period, unselect all)
    // In edit mode, skip past dates — keep their existing period unchanged
    const handleSetAllRangePeriod = (period) => {
        const dates = getRangeDates();
        const todayStr = new Date().toISOString().split('T')[0];
        const editableDates = dates.filter(d => d >= todayStr);
        const allSamePeriod = editableDates.length > 0 && editableDates.every(d => rangeDayPeriods[d] === period);
        if (allSamePeriod) {
            // Unselect only editable dates, keep past dates as-is
            const newPeriods = { ...rangeDayPeriods };
            const newAppts = { ...rangeDayAppointments };
            editableDates.forEach(d => { delete newPeriods[d]; delete newAppts[d]; });
            setRangeDayPeriods(newPeriods);
            setRangeDayAppointments(newAppts);
            return;
        }
        const newPeriods = { ...rangeDayPeriods };
        editableDates.forEach(d => { newPeriods[d] = period; });
        setRangeDayPeriods(newPeriods);

        // Fetch appointment conflicts for all editable dates (bulk check)
        editableDates.forEach(dateStr => {
            let startTime, endTime;
            switch (period) {
                case "forenoon": startTime = "00:00"; endTime = "11:59"; break;
                case "afternoon": startTime = "12:00"; endTime = "23:59"; break;
                case "fullDay": default: startTime = "00:00"; endTime = "23:59"; break;
            }
            const startDate = new Date(`${dateStr}T${startTime}:00`).toISOString();
            const endDate = new Date(`${dateStr}T${endTime}:59`).toISOString();

            setRangeDayLoading(prev => ({ ...prev, [dateStr]: true }));
            dispatch(bookedAppointmentCheck({
                doctorId,
                startDate,
                endDate,
                appointmentStatus: LEAVE_BLOCKING_STATUSES,
                page: 1,
                limit: 100
            })).then((result) => {
                const appts = result.payload?.appointments || [];
                setRangeDayAppointments(prev => ({ ...prev, [dateStr]: appts }));
                setRangeDayLoading(prev => ({ ...prev, [dateStr]: false }));
            }).catch(() => {
                setRangeDayAppointments(prev => ({ ...prev, [dateStr]: [] }));
                setRangeDayLoading(prev => ({ ...prev, [dateStr]: false }));
            });
        });
    };

    // Build days array from range periods — only include days with selected period
    const buildRangeDaysArray = () => {
        return getRangeDates()
            .filter(dateStr => rangeDayPeriods[dateStr])
            .map(dateStr => ({
                date: dateStr,
                ...(PERIOD_TIMES[rangeDayPeriods[dateStr]] || PERIOD_TIMES.fullDay),
                isPublicHoliday: !!isPublicHoliday,
                holidayName: isPublicHoliday ? holidayName.trim() : "",
            }));
    };

    // Check if any range day has appointments
    const hasRangeConflicts = () => {
        return Object.values(rangeDayAppointments).some(appts => appts.length > 0);
    };

    // ---- Range writes: what gets replaced, and putting it back ----
    //
    // A range edit (or a merge with leaves already inside the range) has to
    // delete the old leaves BEFORE the new range is written — the server
    // refuses a second leave on a date. So if the new range is then refused
    // (a duplicate, the hospital's horizon, or the doctor answering "keep the
    // leave off" to booked visits), the doctor would be left with neither
    // leave. The removed leaves are remembered and written back instead.

    const removeLeavesBeingReplaced = async () => {
        const groups = [
            ...(editingGroupId ? (leaveGroups || []).filter(g => g.groupId === editingGroupId) : []),
            ...(mergeConfirmed ? overlapInfo.groups : []),
        ];
        const singles = mergeConfirmed ? overlapInfo.individualLeaves.filter(l => l.leaveId) : [];
        const removed = { groups: [], singles: [] };
        const outcomes = await Promise.allSettled([
            ...groups.map(g => dispatch(deleteLeaveGroup({ doctorId, groupId: g.groupId, year: leaveYear() })).unwrap()
                .then(() => removed.groups.push(g))),
            ...singles.map(l => dispatch(deleteLeave({ leaveId: l.leaveId, doctorId })).unwrap()
                .then(() => removed.singles.push(l))),
        ]);
        const failed = outcomes.find(o => o.status === "rejected");
        return { removed, failure: failed ? failed.reason : null };
    };

    const hasRemovals = (removed) => !!removed && (removed.groups.length > 0 || removed.singles.length > 0);

    const restoreRemovedLeaves = async (removed) => {
        if (!hasRemovals(removed)) return;
        const dayOf = (date, period, extra = {}) => ({
            date,
            ...(period ? PERIOD_TIMES[period] || PERIOD_TIMES.fullDay : { startTime: null, endTime: null }),
            isPublicHoliday: !!extra.isPublicHoliday,
            holidayName: extra.isPublicHoliday ? (extra.holidayName || "") : "",
        });
        const writes = [
            ...removed.groups.map(g => (g.days || []).map(d => dayOf(d.date, d.period, d))),
            ...removed.singles.map(l => [{
                date: l.date,
                startTime: l.startTime,
                endTime: l.endTime,
                isPublicHoliday: !!l.isPublicHoliday,
                holidayName: l.isPublicHoliday ? (l.holidayName || "") : "",
            }]),
        ].filter(days => days.length > 0);
        let failed = false;
        for (const days of writes) {
            try {
                // One write per original leave, so a single day stays a single
                // leave and a group comes back as one group.
                await dispatch(updateLeaveRange({
                    doctorId,
                    leaveData: { fromDate: days[0].date, toDate: days[days.length - 1].date, days, timeZone: selectedDoctor?.timeZone },
                })).unwrap();
            } catch {
                failed = true;
            }
        }
        if (failed) {
            toast.warn(t("leavedatescal.leave_restore_failed", "The earlier leave could not be put back. Please check the leave list and add it again."));
        }
    };

    // Write the range. `cancelAndBook` is the doctor having already chosen
    // "cancel and notify" on the conflicts this screen listed; a plain save that
    // meets visits the screen did not know about is asked about once.
    const submitRangeLeave = async ({ cancelAndBook = false, successText }) => {
        const days = buildRangeDaysArray();
        if (!days.length) return;

        const { removed, failure } = await removeLeavesBeingReplaced();
        if (failure) {
            await restoreRemovedLeaves(removed);
            refreshLeaveLists();
            if (hasRemovals(removed)) handleCancel();
            toast.warn(leaveRefusalSentence(failure, t, i18n.language, t("leavedatescal.save_error_text")));
            return;
        }

        const send = (cancelAndNotify) => (cancelAndBook
            ? dispatch(cancelAndBookLeaveRange({ doctorId, leaveData: { days } })).unwrap()
            : dispatch(updateLeaveRange({
                doctorId,
                leaveData: {
                    fromDate: days[0].date,
                    toDate: days[days.length - 1].date,
                    days,
                    timeZone: selectedDoctor?.timeZone,
                    ...(cancelAndNotify ? { cancelAndNotify: true } : {}),
                },
            })).unwrap());

        let result;
        try {
            try {
                result = await send(false);
            } catch (rejected) {
                const refusal = readLeaveRefusal(rejected);
                if (refusal.kind !== "hasBookings" || cancelAndBook) throw rejected;
                if (!(await askCancelAndNotify(refusal))) {
                    // Keep the leave off: nothing new is written, and whatever
                    // this edit had removed goes back.
                    await restoreRemovedLeaves(removed);
                    if (hasRemovals(removed)) {
                        refreshLeaveLists();
                        handleCancel();
                    }
                    return;
                }
                result = await send(true);
            }
        } catch (rejected) {
            // Put the old leave back only when the SERVER refused the new one.
            // A rejection with no HTTP answer on the leave write (the network
            // dropped, or the thunk's doctor re-read failed after a write that
            // succeeded) may mean the new range IS saved — writing the old one
            // back then could leave two leaves. Refresh and let the list show
            // what is really there.
            if (readLeaveRefusal(rejected).status !== null) {
                await restoreRemovedLeaves(removed);
            }
            refreshLeaveLists();
            if (hasRemovals(removed)) handleCancel();
            toast.warn(leaveRefusalSentence(rejected, t, i18n.language, t(cancelAndBook ? "leavedatescal.cancel_all_error_text" : "leavedatescal.save_error_text")));
            return;
        }

        refreshLeaveLists();
        handleCancel();
        leaveSavedToast(result, successText(days.length));
    };

    const handleSaveRange = async () => {
        if (!selectedDates[0] || !toDate) {
            toast.warn(t("leavedatescal.validation_select_date_range", "Please select both From and To dates."));
            return;
        }

        const selectedRangeDates = getRangeDates().filter(d => rangeDayPeriods[d]);
        if (selectedRangeDates.length === 0) {
            toast.warn(t("leavedatescal.validation_select_at_least_one_period", { defaultValue: "Please select a period (FN/AN/FD) for at least one day." }));
            return;
        }

        if (holidayNameMissing()) return;

        const fromDateStr = selectedRangeDates[0];
        const toDateStr = selectedRangeDates[selectedRangeDates.length - 1];
        const diffDays = selectedRangeDates.length;

        const result = await Swal.fire({
            title: t("leavedatescal.save_confirm_title"),
            text: t("leavedatescal.save_range_confirm_text", { days: diffDays, from: fromDateStr, to: toDateStr, defaultValue: `Set leave for ${diffDays} day(s) from ${fromDateStr} to ${toDateStr}?` }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("leavedatescal.save_confirm_yes"),
            cancelButtonText: t("leavedatescal.save_confirm_no"),
            confirmButtonColor: "#4ABDBD",
            cancelButtonColor: "#d33",
        });

        if (result.isConfirmed) {
            await submitRangeLeave({
                successText: (n) => t("leavedatescal.save_range_success_text", { days: n, defaultValue: `Leave set for ${n} day(s) successfully.` }),
            });
        }
    };

    // Cancel the listed appointments, notify the patients, and set the leave.
    const handleRangeCancelAllAppointments = async () => {
        if (holidayNameMissing()) return;
        const result = await Swal.fire({
            title: t("leavedatescal.cancel_all_title"),
            text: t("leavedatescal.range_cancel_and_notify_text", "The appointments on these days will be cancelled, each patient will be notified, and the leave will be set."),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("leavedatescal.cancel_and_notify_button", "Cancel and notify"),
            cancelButtonText: t("leavedatescal.cancel_button"),
            confirmButtonColor: "#d33",
            cancelButtonColor: "#9ca3af",
        });

        if (result.isConfirmed) {
            await submitRangeLeave({
                cancelAndBook: true,
                successText: () => t("leavedatescal.range_cancel_all_success_text", { defaultValue: "Appointments cancelled and leave set successfully." }),
            });
        }
    };

    // Back to reschedule from range mode
    const handleRangeBackToReschedule = () => {
        Swal.fire({
            title: t("leavedatescal.reschedule_confirm_title"),
            text: t("leavedatescal.reschedule_confirm_text"),
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#4ABDBD",
            cancelButtonColor: "#9ca3af",
            confirmButtonText: t("leavedatescal.reschedule_confirm_button"),
            cancelButtonText: t("leavedatescal.cancel_button"),
        }).then((result) => {
            if (result.isConfirmed) {
                setIsModalOpen(false);
                setSelectedDates([]);
                setToDate(null);
                setLeaveMode("single");
                setHasUnsavedChanges(false);
                setRangeDayPeriods({});
                setRangeDayAppointments({});
                setRangeDayLoading({});
                setMergeConfirmed(false);
                resetHolidayState();
                navigate("/appointment");
            }
        });
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setSelectedDates([]);
        setSelectedTimes({});
        setSelectedPeriod("");
        setEditingDate(null);
        setEditingGroupId(null);
        setHasUnsavedChanges(false);
        setShowAppointments(false);
        setLeaveMode("single");
        setToDate(null);
        setRangeDayPeriods({});
        setRangeDayAppointments({});
        setRangeDayLoading({});
        setMergeConfirmed(false);
        resetHolidayState();
    };

    // The single-day form listed booked visits and the doctor chose to cancel
    // them. ONE request: the leave write with cancelAndNotify, which cancels
    // through the server's shared cancel path (rooms closed, each patient
    // notified) and saves the leave — never a bulk cancel followed by a
    // separate leave save that could fail after the patients were cancelled.
    const handleCancelAllAppointments = async () => {
        const validation = validateTimes();
        if (!validation.isValid) {
            toast.warn(validation.message);
            return;
        }
        if (holidayNameMissing()) return;

        const result = await Swal.fire({
            title: t("leavedatescal.cancel_all_title"),
            text: t("leavedatescal.cancel_and_notify_text", "The booked appointments in this period will be cancelled, each patient will be notified, and the leave will be saved."),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("leavedatescal.cancel_and_notify_button", "Cancel and notify"),
            cancelButtonText: t("leavedatescal.cancel_button"),
            confirmButtonColor: "#d33",
            cancelButtonColor: "#9ca3af",
        });

        if (result.isConfirmed) {
            await saveSingleLeave(
                { ...buildSingleEntry(), cancelAndNotify: true },
                t("leavedatescal.cancel_all_success_text")
            );
        }
    };

    const handleBackToReschedule = () => {
        Swal.fire({
            title: t("leavedatescal.reschedule_confirm_title"),
            text: t("leavedatescal.reschedule_confirm_text"),
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#4ABDBD",
            cancelButtonColor: "#d33",
            confirmButtonText: t("leavedatescal.reschedule_confirm_button"),
            cancelButtonText: t("leavedatescal.cancel_button"),
        }).then((result) => {
            if (result.isConfirmed) {
                // Close the modal
                setIsModalOpen(false);
                setShowAppointments(false);
                setSelectedDates([]);
                setSelectedPeriod("");
                setEditingDate(null);
                setHasUnsavedChanges(false);

                // Navigate to appointments page
                navigate("/appointment");
            }
        });
    };

    const handleTabChange = () => {
        if (hasUnsavedChanges) {
            Swal.fire({
                title: t("leavedatescal.unsaved_changes_title"),
                text: t("leavedatescal.unsaved_changes_text"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: t("leavedatescal.confirm_button"),
                cancelButtonText: t("leavedatescal.cancel_button"),
                customClass: {
                    cancelButton: "custom-cancel-btn",
                    confirmButton: "custom-confirm-btn",
                },
                didRender: () => {
                    const cancelButton = document.querySelector(".swal2-cancel");
                    cancelButton.style.backgroundColor = "#FEE2E2";
                    cancelButton.style.color = "#B91C1C";
                    const confirmButton = document.querySelector(".swal2-confirm");
                    confirmButton.style.backgroundColor = "#4ABDBD";
                    confirmButton.style.color = "#fff";
                    confirmButton.style.outline = "none";
                    confirmButton.style.boxShadow = "none";
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    setHasUnsavedChanges(false);
                }
            });
        }
    };

    const localDayKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    // The choice the footer offers when the pre-check found live visits — the
    // same sentence the server's 409 is turned into.
    const bookingsChoiceText = (count, dates) =>
        leaveBookingsQuestion({ data: { affectedCount: count, conflictDates: dates } }, t, i18n.language).text;

    const getPeriodDisplay = (item) => {
        const { startTime, endTime } = item;
        if (startTime === "00:00" && (endTime === "11:59" || endTime === "12:00")) {
            return (
                <span className="text-xs sm:text-sm flex items-center">
                    <Sun className="mr-1 text-yellow-500" />
                    {t("leavedatescal.forenoon")}
                </span>
            );
        } else if (startTime === "12:00" && endTime === "23:59") {
            return (
                <span className="text-xs sm:text-sm flex items-center">
                    <CloudSun className="mr-1 text-orange-500" />
                    {t("leavedatescal.afternoon")}
                </span>
            );
        } else {
            return (
                <span className="text-xs sm:text-sm flex items-center">
                    <Cloud className="mr-1 text-gray-500" />
                    {t("leavedatescal.full_day")}
                </span>
            );
        }
    };

    return (
        <div className="bg-white h-full" data-testid="leavedatescal-container">
            <div className="mb-6 sm:mb-8">
                {/* Header Row */}
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="leavedatescal-heading">
                        {t("leavedatescal.heading")}
                    </h2>
                    <button
                        onClick={handleAddEditClick}
                        className="group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm"
                        data-testid="leavedatescal-add-edit-button"
                    >
                        <span data-testid="add-edit-text">{t("leavedatescal.add_edit_button")}</span>
                    </button>
                </div>
                <div className="flex justify-center items-center mb-6 gap-3">
                    <button
                        onClick={handlePrevYear}
                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!canGoPrev || loading}
                        data-testid="prev-year-button"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <span className="text-base font-bold text-white bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-3 py-1.5 rounded-lg shadow-md" data-testid="current-year">
                        {currentYearDisplayed}
                    </span>
                    <button
                        onClick={handleNextYear}
                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!canGoNext || loading}
                        data-testid="next-year-button"
                    >
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                <div className="custom-scrollbar max-h-[60vh] p-2" data-testid="leavedatescal-leave-list">
                    {leaveLoading ? (
                        <div className="flex flex-col items-center justify-center py-4 pt-12">
                            <CommonLoader />
                            <span className="text-xl text-[#4ABDBD]">{t('doctorprofile.loading')}</span>
                        </div>
                    ) : (
                        <>
                            {/* Leaves by Month — with bulk groups inline */}
                            {leaves?.months && (() => {
                                // Collect all leaveIds that belong to a group
                                const groupedLeaveIds = new Set();
                                (leaveGroups || []).forEach(g => g.leaveIds.forEach(id => groupedLeaveIds.add(id)));

                                // Short month names matching backend keys
                                const shortMonthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

                                // Map each group to its starting month (short name to match backend)
                                const groupsByMonth = {};
                                (leaveGroups || []).forEach(group => {
                                    const startDate = new Date(group.fromDate + "T00:00:00");
                                    const monthKey = shortMonthNames[startDate.getMonth()];
                                    if (!groupsByMonth[monthKey]) groupsByMonth[monthKey] = [];
                                    groupsByMonth[monthKey].push(group);
                                });

                                // Get all months that have content (individual leaves or groups)
                                const allMonths = new Set([
                                    ...Object.keys(leaves.months).filter(month => {
                                        const hasIndividual = leaves.months[month].some(item => !groupedLeaveIds.has(item.leaveId));
                                        return hasIndividual;
                                    }),
                                    ...Object.keys(groupsByMonth)
                                ]);

                                // Sort by month order
                                const sortedMonths = [...allMonths].sort((a, b) => shortMonthNames.indexOf(a) - shortMonthNames.indexOf(b));

                                return sortedMonths.map((month, monthIndex) => {
                                    const individualLeaves = (leaves.months[month] || []).filter(item => !groupedLeaveIds.has(item.leaveId));
                                    const monthGroups = groupsByMonth[month] || [];

                                    if (individualLeaves.length === 0 && monthGroups.length === 0) return null;

                                    return (
                                    <div key={month} className="mb-6 border-b border-gray-200 pb-6 py-2">
                                        <h3 className="text-lg font-bold text-[#4ABDBD] bg-gradient-to-r from-blue-50 to-cyan-50 px-4 py-3 rounded-xl shadow-sm mb-4" data-testid={`month-${month}`}>
                                            {month}
                                        </h3>

                                        {/* Bulk Leave Groups in this month */}
                                        {monthGroups.length > 0 && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                                {monthGroups.map((group) => {
                                                    const fromDate = new Date(group.fromDate + "T00:00:00");
                                                    const toDateG = new Date(group.toDate + "T00:00:00");
                                                    const today = new Date();
                                                    today.setHours(0, 0, 0, 0);
                                                    const isFutureOrToday = toDateG >= today;
                                                    const periodLabel = (p) => p === "forenoon" ? "FN" : p === "afternoon" ? "AN" : "FD";

                                                    return (
                                                        <div
                                                            key={group.groupId}
                                                            className="relative rounded-xl border border-[#4ABDBD]/20 bg-gradient-to-r from-[#4ABDBD]/5 to-blue-50/50 shadow-sm hover:shadow-md transition-all duration-300 p-4"
                                                        >
                                                            <div className="flex items-center justify-between mb-3">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="bg-gradient-to-r from-[#4ABDBD]/20 to-[#3AA8A8]/20 p-2 rounded-lg">
                                                                        <Calendar className="w-4 h-4 text-[#4ABDBD]" />
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-sm font-bold text-gray-800">
                                                                            {fromDate.toLocaleDateString(i18n.language, { day: "2-digit", month: "short", year: "numeric" })}
                                                                            <span className="text-gray-400 mx-1">→</span>
                                                                            {toDateG.toLocaleDateString(i18n.language, { day: "2-digit", month: "short", year: "numeric" })}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500">{group.days.length} {t("leavedatescal.days", "day(s)")}</p>
                                                                    </div>
                                                                </div>
                                                                {isFutureOrToday && (
                                                                    <div className="flex gap-1.5">
                                                                        <button
                                                                            data-testid={`edit-leave-group-${group.groupId}`}
                                                                            onClick={() => handleEditGroup(group)}
                                                                            className="group/btn text-[#4ABDBD] hover:text-white p-1.5 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-105"
                                                                        >
                                                                            <Edit className="w-3.5 h-3.5" />
                                                                        </button>
                                                                        <button
                                                                            data-testid={`delete-leave-group-${group.groupId}`}
                                                                            onClick={async () => {
                                                                                const result = await Swal.fire({
                                                                                    title: t("leavedatescal.delete_group_title", "Delete Bulk Leave"),
                                                                                    text: t("leavedatescal.delete_group_text", "This will delete all leaves in this group. Are you sure?"),
                                                                                    icon: "warning",
                                                                                    showCancelButton: true,
                                                                                    confirmButtonText: t("leavedatescal.confirm_button", "Yes"),
                                                                                    cancelButtonText: t("leavedatescal.cancel_button", "Cancel"),
                                                                                    confirmButtonColor: "#d33",
                                                                                    cancelButtonColor: "#9ca3af",
                                                                                });
                                                                                if (result.isConfirmed) {
                                                                                    dispatch(deleteLeaveGroup({ doctorId, groupId: group.groupId, year: leaves?.year || currentYearDisplayed }));
                                                                                }
                                                                            }}
                                                                            className="group/btn text-red-500 hover:text-white p-1.5 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                                                        >
                                                                            <Trash2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {group.days.map((day) => (
                                                                    <span
                                                                        key={day.date}
                                                                        className={`text-[10px] font-semibold px-2 py-1 rounded-md ${
                                                                            day.period === "forenoon" ? "bg-yellow-100 text-yellow-700"
                                                                            : day.period === "afternoon" ? "bg-orange-100 text-orange-700"
                                                                            : "bg-[#4ABDBD]/10 text-[#4ABDBD]"
                                                                        }`}
                                                                    >
                                                                        {new Date(day.date + "T00:00:00").toLocaleDateString(i18n.language, { day: "2-digit", month: "short" })} · {periodLabel(day.period)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                            {group.days.some(d => d.isPublicHoliday) && (
                                                                <div className="mt-3 pt-3 border-t border-[#4ABDBD]/10">
                                                                    <span
                                                                        className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded-md"
                                                                        data-testid={`group-holiday-badge-${group.groupId}`}
                                                                    >
                                                                        🏖️ {t("leavedatescal.public_holiday_label", "Public Holiday")}{(() => { const h = group.days.find(d => d.isPublicHoliday); return h && h.holidayName ? ` — ${h.holidayName}` : ""; })()}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Individual leaves (not in any group) */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {individualLeaves.map((item, index) => {
                                                const leaveDate = new Date(item.date);
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                const isFutureOrToday = leaveDate >= today;

                                                return (
                                                    <div
                                                        key={index}
                                                        className="group relative flex items-center gap-2 sm:gap-3 rounded-lg border border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 shadow-sm hover:shadow-md hover:shadow-[#4ABDBD]/20 transition-all duration-300 p-3 sm:p-4 hover:scale-102"
                                                        data-testid={`leave-date-${monthIndex}-${index}`}
                                                    >
                                                        <div className="flex-shrink-0 bg-gradient-to-r from-[#4ABDBD]/20 to-[#3AA8A8]/20 p-2 sm:p-3 rounded-lg shadow-sm">
                                                            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#4ABDBD]" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs sm:text-sm font-bold text-gray-700 group-hover:text-[#4ABDBD] transition-colors duration-300 truncate">
                                                                {new Date(item.date).toLocaleDateString(i18n.language, {
                                                                    day: "2-digit",
                                                                    month: "long",
                                                                    year: "numeric",
                                                                })}
                                                                {item.startTime && item.endTime && (
                                                                    <span className="ml-2 text-xs text-gray-500 group-hover:text-gray-600 transition-colors duration-300">
                                                                        {getPeriodDisplay(item)}
                                                                    </span>
                                                                )}
                                                            </p>
                                                            {item.isPublicHoliday && (
                                                                <span
                                                                    className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-md"
                                                                    data-testid={`leave-holiday-badge-${monthIndex}-${index}`}
                                                                >
                                                                    🏖️ {t("leavedatescal.public_holiday_label", "Public Holiday")}{item.holidayName ? ` — ${item.holidayName}` : ""}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {isFutureOrToday && (
                                                            <div className="flex gap-1 sm:gap-2 ml-auto opacity-70 group-hover:opacity-100 transition-all duration-300">
                                                                <button
                                                                    onClick={() => handleEditSpecific(item)}
                                                                    className="group/btn text-[#4ABDBD] hover:text-white p-1.5 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                                                    data-testid={`edit-leave-${monthIndex}-${index}`}
                                                                >
                                                                    <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(item)}
                                                                    className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                                    data-testid={`delete-leave-${monthIndex}-${index}`}
                                                                >
                                                                    <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    );
                                });
                            })()}
                            {leaveDates.length === 0 && (!leaveGroups || leaveGroups.length === 0) && !loading && (
                                <div className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8" data-testid="no-leave-dates">
                                    <img
                                        data-testid="no-leave-dates-image"
                                        src={emptykey}
                                        alt={t("leavedatescal.no_leave_dates_alt")}
                                        className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-56 lg:h-56 object-contain"
                                    />
                                    <p data-testid="no-leave-dates-text" className="text-gray-500 text-center text-sm sm:text-base md:text-lg">
                                        {t("leavedatescal.no_leave_dates")}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="leavedatescal-modal-title"
                    id="leavedatescal-modal"
                    data-testid="modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="leavedatescal-modal-title"
                                data-testid="modal-title"
                            >
                                {editingDate || editingGroupId ? t("leavedatescal.edit_leave") : t("leavedatescal.add_leave")}
                            </h2>
                            <button
                                id="leavedatescal-modal-close-button"
                                onClick={handleCancel}
                                aria-label={t("leavedatescal.cancel_button")}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar min-w-0">
                            <div className="flex flex-col gap-6 min-w-0">
                                {/* Leave Mode Toggle */}
                                {!editingDate && !editingGroupId && (
                                    <div className="flex items-center gap-3">
                                        <button
                                            data-testid="leave-mode-single-button"
                                            type="button"
                                            onClick={() => { setLeaveMode("single"); setToDate(null); setSelectedDates([]); setSelectedTimes({}); setSelectedPeriod(""); setShowAppointments(false); setRangeDayPeriods({}); setRangeDayAppointments({}); setRangeDayLoading({}); setHasUnsavedChanges(false); setEditingGroupId(null); resetHolidayState(); }}
                                            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${leaveMode === "single" ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            {t("leavedatescal.single_day", "Single Day")}
                                        </button>
                                        <button
                                            data-testid="leave-mode-range-button"
                                            type="button"
                                            onClick={() => { setLeaveMode("range"); setSelectedDates([]); setToDate(null); setSelectedTimes({}); setSelectedPeriod(""); setShowAppointments(false); setRangeDayPeriods({}); setRangeDayAppointments({}); setRangeDayLoading({}); setHasUnsavedChanges(false); setEditingGroupId(null); resetHolidayState(); }}
                                            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${leaveMode === "range" ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            {t("leavedatescal.date_range", "Date Range")}
                                        </button>
                                    </div>
                                )}

                                {/* First Div - Date Picker and Leave Type */}
                                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 shadow-lg border border-blue-100">
                                    <div className={`grid grid-cols-1 ${leaveMode === "single" || (leaveMode === "range" && hasOverlap) ? "lg:grid-cols-2" : "lg:grid-cols-1"} gap-6`}>
                                        {/* Calendar Section */}
                                        <div className="flex flex-col justify-center items-center">
                                            <div className="rounded-xl p-4">
                                                {leaveMode === "single" ? (
                                                    <MultiDatePicker
                                                        numberOfMonths={1}
                                                        multiple={false}
                                                        onlyShowInRangeDates={true}
                                                        className="w-full text-sm sm:text-base"
                                                        value={selectedDates[0] || null}
                                                        onChange={handleDateChange}
                                                        locale={customLocale}
                                                        weekDays={weekDaysArray}
                                                        weekStartDayIndex={1}
                                                        format="YYYY-MM-DD"
                                                        minDate={new Date()}
                                                        maxDate={maxLeaveDate}
                                                        key={`calendar-single-${i18n.language}`}
                                                        mapDays={({ date }) => {
                                                            const formatted = date.format("YYYY-MM-DD");
                                                            const isBooked = leaveDates.some((item) => item.date === formatted);
                                                            let props = {};
                                                            if (isBooked && formatted !== editingDate) {
                                                                props.disabled = true;
                                                                props.style = { color: "#bbb", backgroundColor: "#f8f8f8", cursor: "not-allowed" };
                                                            }
                                                            return props;
                                                        }}
                                                        data-testid="leavedatescal-calendar"
                                                    />
                                                ) : (
                                                    <MultiDatePicker
                                                        numberOfMonths={1}
                                                        range
                                                        onlyShowInRangeDates={true}
                                                        className="w-full text-sm sm:text-base"
                                                        value={selectedDates[0] && toDate ? [selectedDates[0], toDate] : selectedDates[0] ? [selectedDates[0]] : []}
                                                        onChange={(values) => {
                                                            // User-initiated range change resets merge state so the prompt re-evaluates
                                                            setMergeConfirmed(false);
                                                            if (!values || values.length === 0) {
                                                                setSelectedDates([]);
                                                                setToDate(null);
                                                                return;
                                                            }
                                                            const from = new Date(values[0]);
                                                            setSelectedDates([from]);
                                                            if (values.length === 2) {
                                                                const to = new Date(values[1]);
                                                                setToDate(to);
                                                            } else {
                                                                setToDate(null);
                                                            }
                                                            setHasUnsavedChanges(true);
                                                        }}
                                                        locale={customLocale}
                                                        weekDays={weekDaysArray}
                                                        weekStartDayIndex={1}
                                                        format="YYYY-MM-DD"
                                                        minDate={editingGroupId && selectedDates[0] ? selectedDates[0] : new Date()}
                                                        maxDate={maxLeaveDate}
                                                        key={`calendar-range-${i18n.language}-${editingGroupId || ''}`}
                                                        mapDays={({ date }) => {
                                                            const formatted = date.format("YYYY-MM-DD");
                                                            // In edit mode, allow the group's own leave dates
                                                            const isOwnGroupDate = editingGroupLeaveIds.size > 0 && leaveDates.some((item) => item.date === formatted && editingGroupLeaveIds.has(item.leaveId));
                                                            const isMergedDate = mergeConfirmed && lockedRangeDates[formatted] !== undefined;
                                                            const isBooked = leaveDates.some((item) => item.date === formatted) && !isOwnGroupDate && !isMergedDate;
                                                            let props = {};
                                                            if (isBooked) {
                                                                props.disabled = true;
                                                                props.style = { color: "#bbb", backgroundColor: "#f8f8f8", cursor: "not-allowed" };
                                                            } else if (isMergedDate) {
                                                                // Visual hint that this date is being merged in from an existing leave
                                                                props.style = { backgroundColor: "#fef3c7", color: "#92400e", fontWeight: 700 };
                                                            }
                                                            return props;
                                                        }}
                                                        data-testid="leavedatescal-calendar-range"
                                                    />
                                                )}
                                            </div>
                                            {/* Range summary */}
                                            {leaveMode === "range" && selectedDates[0] && toDate && (
                                                <div className="mt-3 bg-white rounded-xl p-4 shadow-sm border border-gray-200 text-center w-full max-w-sm">
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold text-[#4ABDBD]">{selectedDates[0].toLocaleDateString(i18n.language, { day: "2-digit", month: "short", year: "numeric" })}</span>
                                                        <span className="mx-2 text-gray-400">{t("leavedatescal.to_label", "to")}</span>
                                                        <span className="font-semibold text-[#4ABDBD]">{toDate.toLocaleDateString(i18n.language, { day: "2-digit", month: "short", year: "numeric" })}</span>
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {getRangeDates().length} {t("leavedatescal.days_label", "day(s)")}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Merge prompt - Range Mode with existing overlapping leaves */}
                                        {leaveMode === "range" && hasOverlap && (
                                            <div className="flex flex-col justify-center">
                                                <div className={`rounded-xl p-5 border-2 shadow-sm ${mergeConfirmed ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}>
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <AlertCircle className={`w-5 h-5 ${mergeConfirmed ? "text-emerald-600" : "text-amber-600"}`} />
                                                        <h4 className={`text-sm font-bold ${mergeConfirmed ? "text-emerald-700" : "text-amber-700"}`}>
                                                            {mergeConfirmed
                                                                ? t("leavedatescal.merged_leaves_title", "Existing leaves merged")
                                                                : t("leavedatescal.existing_leaves_title", "Existing leave inside this range")}
                                                        </h4>
                                                    </div>
                                                    <p className="text-xs text-gray-600 mb-3">
                                                        {mergeConfirmed
                                                            ? t("leavedatescal.merged_leaves_desc", "These existing leaves are now part of the new range with their original period locked:")
                                                            : t("leavedatescal.existing_leaves_desc", "These leaves already exist inside the selected range:")}
                                                    </p>

                                                    <div className="flex flex-wrap gap-1.5 mb-4 max-h-40 overflow-y-auto custom-scrollbar">
                                                        {overlapInfo.individualLeaves.map(item => {
                                                            const p = periodFromTimes(item.startTime, item.endTime);
                                                            const label = p === "forenoon" ? "FN" : p === "afternoon" ? "AN" : "FD";
                                                            return (
                                                                <span key={item.leaveId || item.date} className={`text-[10px] font-semibold px-2 py-1 rounded-md ${mergeConfirmed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                                                    {new Date(item.date + "T00:00:00").toLocaleDateString(i18n.language, { day: "2-digit", month: "short" })} · {label} · {t("leavedatescal.single_label", "single")}
                                                                </span>
                                                            );
                                                        })}
                                                        {overlapInfo.groups.map(g => g.days.map(d => {
                                                            const label = d.period === "forenoon" ? "FN" : d.period === "afternoon" ? "AN" : "FD";
                                                            return (
                                                                <span key={`${g.groupId}-${d.date}`} className={`text-[10px] font-semibold px-2 py-1 rounded-md ${mergeConfirmed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                                                    {new Date(d.date + "T00:00:00").toLocaleDateString(i18n.language, { day: "2-digit", month: "short" })} · {label} · {t("leavedatescal.range_label", "range")}
                                                                </span>
                                                            );
                                                        }))}
                                                    </div>

                                                    {!mergeConfirmed ? (
                                                        <>
                                                            <p className="text-xs text-gray-700 mb-3 font-medium">
                                                                {t("leavedatescal.merge_question", "Do you want to merge these into your new leave range?")}
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={handleMergeConfirm}
                                                                className="w-full px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg text-sm font-semibold cursor-pointer transition-all shadow-md hover:shadow-lg"
                                                                data-testid="doctor-leave-merge-confirm-button"
                                                            >
                                                                {t("leavedatescal.merge_button", "Merge with new leave")}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleUndoMerge}
                                                            className="w-full px-3 py-2 bg-gradient-to-r from-gray-300 to-gray-400 hover:from-gray-400 hover:to-gray-500 text-white rounded-lg text-sm font-semibold cursor-pointer transition-all shadow-sm"
                                                            data-testid="doctor-leave-undo-merge-button"
                                                        >
                                                            {t("leavedatescal.undo_merge_button", "Undo merge")}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Leave Type Section - Single Mode */}
                                        {leaveMode === "single" && selectedDates.length > 0 && (
                                            <div className="flex flex-col justify-center">
                                                {selectedDates.map((date, index) => {
                                                    // Use local date methods to avoid timezone issues
                                                    const year = date.getFullYear();
                                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                                    const day = String(date.getDate()).padStart(2, '0');
                                                    const formattedDate = `${year}-${month}-${day}`;
                                                    return (
                                                        <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                                                            <p className="text-center text-lg sm:text-xl font-bold text-[#4ABDBD] mb-6">
                                                                {date.toLocaleDateString(i18n.language, {
                                                                    day: "2-digit",
                                                                    month: "short",
                                                                    year: "numeric",
                                                                })}
                                                            </p>
                                                            <div className="flex flex-col gap-3">
                                                                <label
                                                                    className="group flex items-center cursor-pointer text-sm font-semibold px-4 py-3 rounded-xl bg-gradient-to-r from-yellow-50 to-orange-50 hover:from-yellow-100 hover:to-orange-100 transition-all duration-300 shadow-sm hover:shadow-md border border-yellow-200 hover:border-yellow-300"
                                                                    htmlFor={`forenoon-radio-${index}`}
                                                                    data-testid={`forenoon-label-${index}`}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        id={`forenoon-radio-${index}`}
                                                                        name={`period-${index}`}
                                                                        value="forenoon"
                                                                        checked={selectedPeriod === "forenoon"}
                                                                        onChange={() => handlePeriodChange("forenoon", formattedDate)}
                                                                        className="mr-3 accent-[#4ABDBD] scale-110"
                                                                        data-testid={`forenoon-radio-${index}`}
                                                                    />
                                                                    <Sun className="mr-3 text-yellow-500 group-hover:text-yellow-600 transition-colors duration-300" />
                                                                    {t("leavedatescal.forenoon")}
                                                                </label>
                                                                <label
                                                                    className="group flex items-center cursor-pointer text-sm font-semibold px-4 py-3 rounded-xl bg-gradient-to-r from-orange-50 to-red-50 hover:from-orange-100 hover:to-red-100 transition-all duration-300 shadow-sm hover:shadow-md border border-orange-200 hover:border-orange-300"
                                                                    htmlFor={`afternoon-radio-${index}`}
                                                                    data-testid={`afternoon-label-${index}`}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        id={`afternoon-radio-${index}`}
                                                                        name={`period-${index}`}
                                                                        value="afternoon"
                                                                        checked={selectedPeriod === "afternoon"}
                                                                        onChange={() => handlePeriodChange("afternoon", formattedDate)}
                                                                        className="mr-3 accent-[#4ABDBD] scale-110"
                                                                        data-testid={`afternoon-radio-${index}`}
                                                                    />
                                                                    <CloudSun className="mr-3 text-orange-500 group-hover:text-orange-600 transition-colors duration-300" />
                                                                    {t("leavedatescal.afternoon")}
                                                                </label>
                                                                <label
                                                                    className="group flex items-center cursor-pointer text-sm font-semibold px-4 py-3 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 transition-all duration-300 shadow-sm hover:shadow-md border border-blue-200 hover:border-blue-300"
                                                                    htmlFor={`full-day-radio-${index}`}
                                                                    data-testid={`full-day-label-${index}`}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        id={`full-day-radio-${index}`}
                                                                        name={`period-${index}`}
                                                                        value="fullDay"
                                                                        checked={selectedPeriod === "fullDay"}
                                                                        onChange={() => handlePeriodChange("fullDay", formattedDate)}
                                                                        className="mr-3 accent-[#4ABDBD] scale-110"
                                                                        data-testid={`full-day-radio-${index}`}
                                                                    />
                                                                    <Cloud className="mr-3 text-gray-500 group-hover:text-[#4ABDBD] transition-colors duration-300" />
                                                                    {t("leavedatescal.full_day")}
                                                                </label>
                                                            </div>

                                                            {/* Public holiday option — shown once a period is chosen */}
                                                            {selectedPeriod && (
                                                                <div className="mt-4 pt-4 border-t border-gray-100">
                                                                    <label className="flex items-center gap-2 cursor-pointer" data-testid={`public-holiday-toggle-${index}`}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isPublicHoliday}
                                                                            onChange={(e) => handleHolidayToggle(e.target.checked)}
                                                                            className="accent-[#4ABDBD] scale-110"
                                                                            data-testid={`public-holiday-checkbox-${index}`}
                                                                        />
                                                                        <span className="text-sm font-semibold text-gray-700">{t("leavedatescal.mark_public_holiday", "Mark as public holiday")}</span>
                                                                    </label>
                                                                    {isPublicHoliday && (
                                                                        <input
                                                                            type="text"
                                                                            value={holidayName}
                                                                            onChange={(e) => { setHolidayName(e.target.value); setHasUnsavedChanges(true); }}
                                                                            placeholder={t("leavedatescal.public_holiday_name_placeholder", "Name of this public holiday")}
                                                                            className="mt-3 w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD] outline-none"
                                                                            data-testid={`public-holiday-name-${index}`}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Range Day Timeline with Period Selection */}
                                {leaveMode === "range" && selectedDates[0] && toDate && (() => {
                                    const rangeDates = getRangeDates();
                                    return (
                                        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200 max-w-full overflow-hidden">
                                            {/* Set All Period */}
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="text-sm font-bold text-gray-700">{t("leavedatescal.set_period_for_days", "Set period for each day")}</h4>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500">{t("leavedatescal.set_all", "Set all")}:</span>
                                                    {["forenoon", "afternoon", "fullDay"].map(p => (
                                                        <button
                                                            key={p}
                                                            type="button"
                                                            onClick={() => handleSetAllRangePeriod(p)}
                                                            className="px-2 py-1 text-xs font-semibold rounded-lg border border-gray-200 hover:bg-[#4ABDBD] hover:text-white hover:border-[#4ABDBD] transition-all duration-200"
                                                            data-testid={`doctor-leave-set-all-${p}-button`}
                                                        >
                                                            {p === "forenoon" ? "FN" : p === "afternoon" ? "AN" : "FD"}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Horizontal Scrollable Timeline */}
                                            <div className="w-full pt-2 pb-4 custom-scrollbar1" style={{ maxWidth: '100%' }}>
                                                <div className="flex min-w-max">
                                                    {rangeDates.map((dateStr, idx) => {
                                                        const dayDate = new Date(dateStr + "T00:00:00");
                                                        const dayLabel = dayDate.toLocaleDateString(i18n.language, { weekday: "short" });
                                                        const dayNum = dayDate.getDate();
                                                        const currentPeriod = rangeDayPeriods[dateStr];
                                                        const dayAppts = rangeDayAppointments[dateStr] || [];
                                                        const isLoading = rangeDayLoading[dateStr];
                                                        const todayDate = new Date();
                                                        todayDate.setHours(0, 0, 0, 0);
                                                        const isPast = dayDate < todayDate;
                                                        const isLocked = lockedRangeDates[dateStr] !== undefined;

                                                        return (
                                                            <div key={dateStr} className={`flex flex-col items-center min-w-[72px]  py-1 ${isPast ? 'opacity-50' : ''} ${isLocked ? 'bg-amber-50/60 rounded-lg' : ''}`}>
                                                                {/* Date info */}
                                                                <div className="text-center mb-2">
                                                                    <p className="text-[10px] text-gray-400 font-medium">{dayLabel}</p>
                                                                    <p className={`text-sm font-bold ${isPast ? 'text-gray-400' : isLocked ? 'text-amber-700' : 'text-gray-800'}`}>{dayNum}</p>
                                                                    <p className={`text-[8px] font-bold uppercase tracking-wide mt-0.5 ${isLocked ? 'text-amber-600' : 'invisible'}`}>{t("leavedatescal.locked_label", "Existing")}</p>
                                                                </div>

                                                                {/* Timeline dot and line */}
                                                                <div className="flex items-center w-full mb-2">
                                                                    {idx > 0 && <div className="flex-1 h-0.5 bg-gray-200"></div>}
                                                                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isPast ? 'bg-gray-300' : isLocked ? 'bg-amber-500' : currentPeriod ? 'bg-[#4ABDBD]' : 'bg-gray-300'} transition-colors duration-200`}></div>
                                                                    {idx < rangeDates.length - 1 && <div className="flex-1 h-0.5 bg-gray-200"></div>}
                                                                </div>

                                                                {/* FN / AN / FD radio buttons */}
                                                                <div className="flex flex-col gap-1">
                                                                    {["forenoon", "afternoon", "fullDay"].map(period => (
                                                                        <button
                                                                            key={period}
                                                                            type="button"
                                                                            disabled={isPast}
                                                                            onClick={() => !isPast && handleRangeDayPeriodChange(dateStr, period)}
                                                                            title={isLocked ? t("leavedatescal.locked_period_tooltip", "Pre-filled from the existing leave — you can change it") : undefined}
                                                                            data-testid={`doctor-leave-range-day-${dateStr}-${period}-button`}
                                                                            className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition-all duration-200 ${
                                                                                isPast
                                                                                    ? currentPeriod === period ? 'bg-gray-300 text-white cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                                    : currentPeriod === period
                                                                                        ? period === "forenoon" ? `bg-yellow-400 text-white${isLocked ? ' ring-2 ring-amber-500' : ''}`
                                                                                        : period === "afternoon" ? `bg-orange-400 text-white${isLocked ? ' ring-2 ring-amber-500' : ''}`
                                                                                        : `bg-[#4ABDBD] text-white${isLocked ? ' ring-2 ring-amber-500' : ''}`
                                                                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                                                            }`}
                                                                        >
                                                                            {period === "forenoon" ? "FN" : period === "afternoon" ? "AN" : "FD"}
                                                                        </button>
                                                                    ))}
                                                                </div>

                                                                {/* Appointment indicator */}
                                                                {isLoading && (
                                                                    <span className="mt-1 w-3 h-3 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                                )}
                                                                {!isLoading && dayAppts.length > 0 && (
                                                                    <span className="mt-1 text-[9px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">
                                                                        {dayAppts.length}
                                                                    </span>
                                                                )}
                                                                {!isLoading && currentPeriod && dayAppts.length === 0 && (
                                                                    <span className="mt-1 text-[9px] text-green-500">✓</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Whole-range public holiday — one name covers every selected day */}
                                            {rangeDates.some(d => rangeDayPeriods[d]) && (
                                                <div className="mt-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4 border border-[#4ABDBD]/20">
                                                    <label className="flex items-center gap-2 cursor-pointer" data-testid="range-holiday-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={isPublicHoliday}
                                                            onChange={(e) => handleHolidayToggle(e.target.checked)}
                                                            className="accent-[#4ABDBD] scale-110"
                                                            data-testid="range-holiday-checkbox"
                                                        />
                                                        <span className="text-sm font-semibold text-gray-700">{t("leavedatescal.mark_public_holiday", "Mark as public holiday")}</span>
                                                    </label>
                                                    {isPublicHoliday && (
                                                        <input
                                                            type="text"
                                                            value={holidayName}
                                                            onChange={(e) => { setHolidayName(e.target.value); setHasUnsavedChanges(true); }}
                                                            placeholder={t("leavedatescal.public_holiday_name_placeholder", "Name of this public holiday")}
                                                            className="mt-3 w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD] outline-none"
                                                            data-testid="range-holiday-name"
                                                        />
                                                    )}
                                                </div>
                                            )}

                                            {/* Show appointments for days that have conflicts */}
                                            {Object.entries(rangeDayAppointments).some(([, appts]) => appts.length > 0) && (
                                                <div className="mt-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 border border-red-200">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <AlertCircle className="text-red-600 w-5 h-5" />
                                                        <h4 className="text-sm font-bold text-red-600">{t("leavedatescal.conflicting_appointments", "Appointments on selected days")}</h4>
                                                    </div>
                                                    <div className="max-h-40 overflow-y-auto custom-scrollbar space-y-2">
                                                        {Object.entries(rangeDayAppointments)
                                                            .filter(([, appts]) => appts.length > 0)
                                                            .map(([dateStr, appts]) => (
                                                                <div key={dateStr}>
                                                                    <p className="text-xs font-bold text-gray-700 mb-1">{dateStr}</p>
                                                                    {appts.map((appt, i) => (
                                                                        <div key={i} className="bg-white px-3 py-2 rounded-lg text-xs text-gray-600 border border-gray-100 mb-1">
                                                                            <WhoLabel role="patient" name={[appt.patientName, appt.patientSurname].filter(Boolean).join(' ')} nameClassName="font-semibold text-gray-700" /> — {formatAppointmentTime(appt)} (<MasterLabel type="masterSpecialty" id={appt.SpecialtyId} field="name" fallback={appt.specialtyName} />)
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Second Div - Appointment List (Single mode only) */}
                                {leaveMode === "single" && selectedDates.length > 0 && (
                                    <div className="w-full">
                                        {bookedAppointmentsLoading && (
                                            <div className="flex flex-col items-center justify-center py-8">
                                                <CommonLoader />
                                                <span className="text-sm text-[#4ABDBD] mt-2">{t('leavedatescal.loading_appointments')}</span>
                                            </div>
                                        )}

                                        {showAppointments && bookedAppointments && bookedAppointments.length > 0 && !bookedAppointmentsLoading && (
                                            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 shadow-lg border border-red-200">
                                                <div className="flex items-center gap-2 mb-6">
                                                    <AlertCircle className="text-red-600 w-6 h-6" />
                                                    <h3 className="text-xl font-bold text-red-600">
                                                        {t("leavedatescal.booked_appointments")} ({bookedAppointments.length})
                                                    </h3>
                                                </div>
                                                <div className="max-h-64 overflow-y-auto custom-scrollbar space-y-3">
                                                    {bookedAppointments.map((appointment, idx) => (
                                                        <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
                                                            <div className="flex justify-between items-start">
                                                                <div>
                                                                    <p className="text-base font-semibold text-gray-800">
                                                                        {appointment.patientName} {appointment.patientSurname}
                                                                    </p>
                                                                    <p className="text-sm text-gray-600 mt-1">
                                                                        <span className="font-medium">{t("leavedatescal.appointment_time_label")}</span> {formatAppointmentTime(appointment)}
                                                                    </p>
                                                                    <p className="text-sm text-gray-600">
                                                                        <span className="font-medium">{t("leavedatescal.appointment_specialty_label")}</span> <MasterLabel type="masterSpecialty" id={appointment.SpecialtyId} field="name" fallback={appointment.specialtyName} />
                                                                    </p>
                                                                    {appointment.patientEmail && (
                                                                        <p className="text-xs text-gray-500 mt-1">
                                                                            {appointment.patientEmail}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <div className="ml-4">
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                        {t(`leavedatescal.${appointment.status}`, appointment.status)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-b-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30">
                            {/* Single mode with appointments */}
                            {leaveMode === "single" && showAppointments && bookedAppointments && bookedAppointments.length > 0 ? (
                                <div className="flex flex-col gap-3">
                                <p data-testid="leave-bookings-choice" className="text-sm font-medium text-red-700">
                                    {bookingsChoiceText(bookedAppointments.length, selectedDates[0] ? [localDayKey(selectedDates[0])] : [])}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        data-testid="cancel-all-appointments-button"
                                        onClick={handleCancelAllAppointments}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm cursor-pointer"
                                    >
                                        {t("leavedatescal.cancel_and_notify_button", "Cancel and notify")}
                                    </button>
                                    <button
                                        data-testid="back-to-reschedule-button"
                                        onClick={handleBackToReschedule}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm cursor-pointer"
                                    >
                                        {t("leavedatescal.back_to_reschedule")}
                                    </button>
                                    <button
                                        data-testid="keep-leave-off-button"
                                        type="button"
                                        onClick={handleCancel}
                                        className="flex-1 px-4 py-2 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition-all duration-300 font-semibold text-sm cursor-pointer"
                                    >
                                        {t("leavedatescal.keep_leave_off_button", "Keep the leave off")}
                                    </button>
                                </div>
                                </div>
                            ) : leaveMode === "range" && selectedDates[0] && toDate && hasRangeConflicts() ? (
                                /* Range mode with conflicts */
                                <div className="flex flex-col gap-3">
                                <p data-testid="range-leave-bookings-choice" className="text-sm font-medium text-red-700">
                                    {bookingsChoiceText(
                                        Object.values(rangeDayAppointments).reduce((sum, appts) => sum + appts.length, 0),
                                        Object.entries(rangeDayAppointments).filter(([, appts]) => appts.length > 0).map(([d]) => d).sort()
                                    )}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        data-testid="range-cancel-all-appointments-button"
                                        onClick={handleRangeCancelAllAppointments}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm cursor-pointer"
                                    >
                                        {t("leavedatescal.cancel_and_notify_button", "Cancel and notify")}
                                    </button>
                                    <button
                                        data-testid="range-back-to-reschedule-button"
                                        onClick={handleRangeBackToReschedule}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm cursor-pointer"
                                    >
                                        {t("leavedatescal.back_to_reschedule")}
                                    </button>
                                    <button
                                        data-testid="range-keep-leave-off-button"
                                        type="button"
                                        onClick={handleCancel}
                                        className="flex-1 px-4 py-2 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition-all duration-300 font-semibold text-sm cursor-pointer"
                                    >
                                        {t("leavedatescal.keep_leave_off_button", "Keep the leave off")}
                                    </button>
                                </div>
                                </div>
                            ) : (
                                /* Regular Cancel/Save buttons */
                                <div className="flex justify-end gap-4">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                        data-testid="cancel-button"
                                    >
                                        {t("leavedatescal.cancel_button")}
                                    </button>
                                    <button
                                        type="button"
                                        className={`group flex items-center justify-center gap-2 px-4 py-2 rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm ${
                                            leaveMode === "range"
                                                ? (!selectedDates[0] || !toDate || !getRangeDates().some(d => rangeDayPeriods[d]) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white hover:shadow-xl hover:scale-105 cursor-pointer')
                                                : (selectedDates.length === 0 || !selectedPeriod ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white hover:shadow-xl hover:scale-105 cursor-pointer')
                                        }`}
                                        onClick={leaveMode === "range" ? handleSaveRange : handleSave}
                                        disabled={leaveMode === "range" ? (!selectedDates[0] || !toDate || !getRangeDates().some(d => rangeDayPeriods[d])) : (selectedDates.length === 0 || !selectedPeriod)}
                                        data-testid="submit-button"
                                    >
                                        {t("leavedatescal.save_button")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Leavedatescal;